import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustom, useCustomMutation } from "@refinedev/core";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiUrlV2 } from "@/consts";
import { openLiveHost } from "@/utils/openLiveHost";
import { fmtWallDate, fmtWallTime } from "../scSchedule/time";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// GL-000 DCC: the platform's fitness class schedule. Same engine as S&C, but
// classes are owner-wide — there is no group column because there is no group.

type FitnessSession = {
  id: string;
  title: string | null;
  startsAt: string;
  endsAt: string;
  seriesID: string | null;
  status: "SCHEDULED" | "CANCELLED";
  categories?: { id: string; title: string }[];
};

export function FitnessScheduleList() {
  const navigate = useNavigate();
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf("day"),
    dayjs().add(8, "week").endOf("day"),
  ]);

  const from = range[0].toISOString();
  const to = range[1].toISOString();

  const { data, isLoading, refetch } = useCustom<{ data: FitnessSession[] }>({
    url: `${apiUrlV2}/schedule/admin/fitness`,
    method: "get",
    config: { query: { from, to } },
  });
  const rows = data?.data?.data ?? [];

  const { mutate: removeMutate } = useCustomMutation();
  const { mutate: startLive } = useCustomMutation();
  const [startingId, setStartingId] = useState<string | null>(null);

  // Mint (or reuse) the FITNESS live row for this occurrence and open the host
  // broadcast view. Members who pass the programme gate then see a Join button.
  function goLive(session: FitnessSession) {
    setStartingId(session.id);
    startLive(
      {
        url: `${apiUrlV2}/live`,
        method: "post",
        values: {
          liveType: "FITNESS",
          scheduleSessionID: session.id,
          liveStatus: "STARTED",
        },
      },
      {
        onSuccess: (res: any) => {
          const room = res?.data?.data?.roomID;
          if (!room) {
            message.error("Live started but no room id was returned");
            return;
          }
          openLiveHost({
            roomID: room,
            liveID: res?.data?.data?.id,
            hostName: session.title || "Fitness class",
          });
          message.success("Live started — opening host view");
        },
        onError: (err: any) =>
          message.error(err?.response?.data?.error || err?.message || "Could not start live"),
        onSettled: () => setStartingId(null),
      },
    );
  }

  function remove(session: FitnessSession, scope: "this" | "all_future") {
    const qs = scope === "all_future" ? "?scope=all_future" : "?scope=this";
    removeMutate(
      {
        url: `${apiUrlV2}/schedule/admin/fitness/${session.id}${qs}`,
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          message.success(scope === "all_future" ? "Series ended" : "Class cancelled");
          refetch();
        },
        onError: (err: any) =>
          message.error(err?.response?.data?.error || err?.message || "Could not delete"),
      },
    );
  }

  const columns = [
    {
      title: "Date",
      dataIndex: "startsAt",
      render: (v: string) => fmtWallDate(v),
      sorter: (a: FitnessSession, b: FitnessSession) => a.startsAt.localeCompare(b.startsAt),
      defaultSortOrder: "ascend" as const,
    },
    {
      title: "Time",
      render: (_: any, r: FitnessSession) =>
        `${fmtWallTime(r.startsAt)}–${fmtWallTime(r.endsAt)}`,
    },
    {
      title: "Category",
      render: (_: any, r: FitnessSession) =>
        r.categories?.length ? (
          <Space size={4} wrap>
            {r.categories.map((c) => (
              <Tag color="purple" key={c.id}>
                {c.title}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (t: string | null) => t || <Text type="secondary">—</Text>,
    },
    {
      title: "Recurrence",
      dataIndex: "seriesID",
      render: (s: string | null) =>
        s ? <Tag color="blue">Recurring</Tag> : <Tag>One-off</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s: string) =>
        s === "CANCELLED" ? <Tag color="red">Cancelled</Tag> : <Tag color="green">Scheduled</Tag>,
    },
    {
      title: "Actions",
      render: (_: any, r: FitnessSession) => (
        <Space>
          <Button
            size="small"
            danger
            type="primary"
            loading={startingId === r.id}
            disabled={r.status === "CANCELLED"}
            onClick={() => goLive(r)}
          >
            ● Go live
          </Button>
          <Button size="small" onClick={() => navigate(`/fitness-schedule/edit/${r.id}`)}>
            Edit
          </Button>
          <Popconfirm
            title="Cancel this class?"
            onConfirm={() => remove(r, "this")}
            okText="Cancel class"
          >
            <Button size="small" danger>
              Cancel
            </Button>
          </Popconfirm>
          {r.seriesID && (
            <Popconfirm
              title="End the whole series?"
              description="Removes this and all future occurrences."
              onConfirm={() => remove(r, "all_future")}
              okText="End series"
            >
              <Button size="small">End series</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Fitness Sessions
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/fitness-schedule/create")}
        >
          Create class
        </Button>
      </Space>

      <Space style={{ marginBottom: 16 }}>
        <Text type="secondary">Showing</Text>
        <RangePicker
          value={range}
          allowClear={false}
          onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
        />
      </Space>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: <Empty description="No fitness classes in this range" /> }}
      />
    </Card>
  );
}
